// In-memory search index for the register — avoids Dexie cursor scans over ~45k products on every
// keystroke. Built once from IndexedDB on open, kept fresh incrementally by the 60s delta sync.
//
// Per product record:
//   blob  = lowercased "title + variant names + all codes" → substring name/code search
//   codes = array of lowercased EXACT code tokens (product upc/custom_code + each variant upc/custom_code) → scan
//   catIds = category ids (for future category-scoped search)
//   active = not disabled / not deleted
//
// Search returns product IDS only; the caller hydrates full rows via db.products.bulkGet (fast PK lookup).

import { db } from "./db";

const records = new Map(); // id(string) -> record

const lc = (v) => String(v == null ? "" : v).toLowerCase();
// custom codes can be pipe-delimited; variant fields are comma-joined → split on both
const tokens = (v) => String(v == null ? "" : v).split(/[,|]/).map((s) => s.trim()).filter(Boolean);

function buildRecord(p) {
  const codes = [];
  if (p.upc) codes.push(lc(p.upc));
  tokens(p.custom_code).forEach((c) => codes.push(c.toLowerCase()));
  tokens(p.vupc).forEach((c) => codes.push(c.toLowerCase()));
  tokens(p.vcustom_code).forEach((c) => codes.push(c.toLowerCase()));
  const blob = lc([p.title, p.vvariant, p.upc, p.custom_code, p.vupc, p.vcustom_code].filter(Boolean).join(" "));
  const active = p.disable !== "1" && p.is_deleted !== "1";
  return { id: String(p.id), blob, codes, active };
}

export function clearIndex() {
  records.clear();
}

export function upsertProducts(products) {
  for (const p of products || []) if (p && p.id != null) records.set(String(p.id), buildRecord(p));
}

export function removeProducts(ids) {
  for (const id of ids || []) records.delete(String(id));
}

// Full (re)build from IndexedDB — call once when the register opens (persisted catalog, in-memory index empty).
export async function buildFromDb() {
  records.clear();
  const all = await db.products.toArray();
  upsertProducts(all);
  return records.size;
}

export function isBuilt() {
  return records.size > 0;
}

// Substring match across name + variant names + codes. Returns up to `limit` product ids.
export function searchNames(query, limit = 50) {
  const q = lc(query).trim();
  if (!q) return [];
  const out = [];
  for (const r of records.values()) {
    if (!r.active) continue;
    if (r.blob.includes(q)) {
      out.push(r.id);
      if (out.length >= limit) break;
    }
  }
  return out;
}

// Exact code match (barcode/custom-code scan) against product or any variant token. Returns first id.
export function findCode(code) {
  const c = lc(code).trim();
  if (!c) return null;
  for (const r of records.values()) {
    if (!r.active) continue;
    if (r.codes.includes(c)) return r.id;
  }
  return null;
}
